import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Files, 
  Receipt, 
  ChartBar,
  ArrowRight,
  Copy,
  Link as LinkIcon,
  Clock,
  CheckCircle,
  PaperPlaneTilt,
  Briefcase,
  CalendarBlank,
  Target,
  TrendUp
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate sample revenue data for demo when no real data exists
const generateSampleRevenues = (currentDay) => {
  const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
  const revenues = Array(daysInMonth).fill(0);
  
  // Simulate random invoice peaks on certain days
  const peakDays = [3, 7, 12, 15, 18, 21]; // Days with signed invoices
  const amounts = [2500, 1800, 3200, 1500, 2100, 1400];
  
  peakDays.forEach((day, i) => {
    if (day <= currentDay && day <= daysInMonth) {
      revenues[day - 1] = amounts[i];
    }
  });
  
  return revenues;
};

// Animated counter for currency
const AnimatedCurrency = ({ value }) => {
  const [display, setDisplay] = useState(0);
  
  useEffect(() => {
    const duration = 1200;
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplay(value);
        clearInterval(timer);
      } else {
        setDisplay(current);
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return (
    <span>
      {Math.round(display).toLocaleString('fr-FR')}
    </span>
  );
};

// Animated counter
const AnimatedNumber = ({ value, decimals = 0, suffix = '' }) => {
  const [display, setDisplay] = useState(0);
  
  useEffect(() => {
    const duration = 1200;
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplay(value);
        clearInterval(timer);
      } else {
        setDisplay(current);
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return (
    <span>
      {display.toFixed(decimals).replace('.', ',')}{suffix}
    </span>
  );
};

// Vertical Bar Chart - 1 bar per day, CUMULATIVE revenue
const VerticalBarChart = ({ dailyRevenues, currentDay, objective }) => {
  const daysInMonth = dailyRevenues?.length || 31;
  
  // Calculate cumulative revenues
  const cumulativeRevenues = [];
  let runningTotal = 0;
  for (let i = 0; i < daysInMonth; i++) {
    runningTotal += (dailyRevenues?.[i] || 0);
    cumulativeRevenues.push(runningTotal);
  }
  
  const totalRevenue = cumulativeRevenues[currentDay - 1] || 0;
  const maxValue = Math.max(objective, totalRevenue, 1);
  
  // Get bar height based on cumulative revenue up to that day
  const getBarHeight = (index) => {
    if (index >= currentDay) return 15; // Future days - minimal height
    const cumulative = cumulativeRevenues[index];
    if (cumulative === 0) return 15;
    // Scale to max height based on objective or current total
    return Math.min(15 + (cumulative / maxValue) * 85, 100);
  };
  
  // Get bar color based on position
  const getBarColor = (index) => {
    if (index >= currentDay) return 'bg-slate-200 dark:bg-slate-700'; // Future days
    
    const progress = index / daysInMonth;
    if (progress < 0.25) return 'bg-slate-300 dark:bg-slate-500';
    if (progress < 0.45) return 'bg-amber-300';
    if (progress < 0.65) return 'bg-amber-400';
    if (progress < 0.85) return 'bg-orange-400';
    return 'bg-orange-500';
  };
  
  const now = new Date();
  const percentage = objective > 0 ? Math.round((totalRevenue / objective) * 100) : 0;
  
  return (
    <div className="relative">
      {/* Bar chart */}
      <div className="flex items-end justify-between gap-1 h-28">
        {Array.from({ length: daysInMonth }).map((_, index) => {
          const height = getBarHeight(index);
          return (
            <motion.div
              key={index}
              initial={{ height: 0 }}
              animate={{ height: `${height}%` }}
              transition={{ delay: index * 0.02, duration: 0.3 }}
              className={`w-1.5 rounded-full ${getBarColor(index)}`}
              title={`Jour ${index + 1}: ${cumulativeRevenues[index]?.toLocaleString('fr-FR') || 0} € (cumul)`}
            />
          );
        })}
      </div>
      
      {/* End marker */}
      <div className="absolute left-0 top-8 flex items-center gap-1">
        <span className="text-sm font-medium text-muted-foreground">End</span>
        <div className="w-1.5 h-1.5 rounded-full bg-slate-800 dark:bg-slate-200" />
      </div>
      <div className="absolute left-0 top-14 text-xs text-muted-foreground">
        {new Date(now.getFullYear(), now.getMonth() + 1, 0).toLocaleDateString('fr-FR')}
      </div>
      
      {/* Current date marker on the right */}
      <div className="absolute right-0 top-0 text-right">
        <div className="flex items-center gap-1 justify-end">
          <span className="text-2xl font-bold text-foreground">{currentDay}</span>
          <div className="w-1.5 h-1.5 rounded-full bg-slate-800 dark:bg-slate-200" />
        </div>
        <span className="text-xs text-muted-foreground uppercase tracking-wider">
          {now.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '')}
        </span>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingItems, setPendingItems] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, requestsRes, quotesRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/statistics`),
        axios.get(`${API}/quote-requests`),
        axios.get(`${API}/quotes`),
        axios.get(`${API}/invoices`)
      ]);

      setStats(statsRes.data);
      
      const pending = [];
      requestsRes.data.forEach(r => {
        if (r.status === 'pending') pending.push({ type: 'request', ...r });
      });
      quotesRes.data.forEach(q => {
        if (q.status === 'draft') pending.push({ type: 'quote', ...q });
      });
      
      setPendingItems(pending.slice(0, 4));
      setRecentQuotes(quotesRes.data.slice(0, 4));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyFormLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/devis`);
    toast.success('Lien copié');
  };

  // Calculate monthly revenue from stats
  const monthlyRevenue = stats?.monthly_revenue || 0;
  const monthlyObjective = stats?.monthly_objective || 10000;
  const currentDay = new Date().getDate();
  
  // Use real daily revenues or generate sample data for demo
  const realDailyRevenues = stats?.daily_revenues || [];
  const hasRealData = realDailyRevenues.some(r => r > 0);
  
  // If no real data, show sample data to demonstrate the visualization
  const dailyRevenues = hasRealData ? realDailyRevenues : generateSampleRevenues(currentDay);
  const displayRevenue = hasRealData ? monthlyRevenue : dailyRevenues.reduce((a, b) => a + b, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-3 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const totalRevenue = stats?.total_revenue || 0;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="py-6 space-y-6"
    >
      {/* Main KPI - Monthly Revenue */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="premium-card p-8"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-sm text-muted-foreground mb-2">CA du mois</p>
            <div className="flex items-baseline gap-2">
              <span className="kpi-display">
                <AnimatedCurrency value={displayRevenue} />
              </span>
              <span className="kpi-display-small">€</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              <Target size={14} className="inline mr-1" weight="duotone" />
              Objectif: {monthlyObjective.toLocaleString('fr-FR')} €
            </p>
          </div>
          
          <div className="flex gap-2">
            <button className="icon-btn" onClick={() => navigate('/statistics')}>
              <ChartBar size={20} />
            </button>
            <button className="icon-btn icon-btn-filled" onClick={() => navigate('/compta')}>
              <Receipt size={20} />
            </button>
          </div>
        </div>
        
        {/* Vertical Bar Chart - Daily revenues */}
        <VerticalBarChart 
          dailyRevenues={dailyRevenues} 
          currentDay={currentDay}
          objective={monthlyObjective} 
        />
      </motion.div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="premium-card premium-card-hover p-6"
          onClick={() => navigate('/quotes')}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Files size={20} className="text-amber-600" weight="duotone" />
            </div>
            <span className="text-sm text-muted-foreground">Devis créés</span>
          </div>
          <p className="text-3xl font-bold text-foreground">
            {stats?.total_quotes || 0}
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="premium-card premium-card-hover p-6"
          onClick={() => navigate('/invoices')}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <CheckCircle size={20} className="text-emerald-600" weight="duotone" />
            </div>
            <span className="text-sm text-muted-foreground">Signés</span>
          </div>
          <p className="text-3xl font-bold text-foreground">
            {stats?.signed_quotes || 0}
          </p>
        </motion.div>
      </div>

      {/* Revenue Card - Glass style */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="glass-card rounded-3xl p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Chiffre d'affaires</p>
            <p className="text-2xl font-bold text-foreground">
              <AnimatedNumber value={totalRevenue} decimals={0} suffix=" €" />
            </p>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
            <Receipt size={24} className="text-white" weight="fill" />
          </div>
        </div>
      </motion.div>

      {/* Building Plan Card */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="premium-card overflow-hidden"
      >
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-foreground">À traiter</h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarBlank size={16} />
              <span>{new Date().toLocaleDateString('fr-FR')}</span>
            </div>
          </div>
        </div>
        
        {pendingItems.length === 0 ? (
          <div className="px-6 pb-6 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-3">
              <CheckCircle size={32} className="text-emerald-600" weight="duotone" />
            </div>
            <p className="text-muted-foreground">Tout est à jour !</p>
          </div>
        ) : (
          <div className="px-4 pb-4 space-y-2">
            {pendingItems.map((item, index) => (
              <motion.div
                key={item.request_id || item.quote_id || index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.05 }}
                onClick={() => {
                  if (item.type === 'request') navigate('/requests');
                  else navigate(`/quotes/${item.quote_id}`);
                }}
                className="flex items-center gap-4 p-3 rounded-2xl bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  item.type === 'request' 
                    ? 'bg-blue-100 dark:bg-blue-900/30' 
                    : 'bg-amber-100 dark:bg-amber-900/30'
                }`}>
                  {item.type === 'request' 
                    ? <PaperPlaneTilt size={20} className="text-blue-600" weight="duotone" />
                    : <Files size={20} className="text-amber-600" weight="duotone" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{item.client_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.type === 'request' ? 'Nouvelle demande' : `Devis ${item.quote_number}`}
                  </p>
                </div>
                <ArrowRight size={18} className="text-muted-foreground" />
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* CTA Card */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="premium-card bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-black/70 mb-1">Partagez avec vos clients</p>
            <p className="font-semibold text-black">Formulaire de demande</p>
          </div>
          <button
            onClick={copyFormLink}
            className="w-12 h-12 rounded-2xl bg-black flex items-center justify-center transition-transform active:scale-95"
          >
            <Copy size={22} className="text-white" />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
